import { Button } from "@/modules/shadcn/ui/button";
import { Camera } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface NoCameraConfiguredProps {
  onOpenSettings?: () => void;
  /**
   * Tightens spacing and type so the screen fits inside the 16:9 preview box
   * (Run → Inference) instead of owning the whole page (Capture).
   */
  compact?: boolean;
}

/**
 * Cameras were detected at the project's camera API URL, but the project has
 * no camera configured (project.cameraMxid is null). There is deliberately no
 * fallback — the camera must be picked explicitly in the project settings.
 */
export const NoCameraConfigured = ({
  onOpenSettings,
  compact,
}: NoCameraConfiguredProps) => {
  const { t } = useTranslation("camera");
  const cx = {
    root: compact
      ? "flex h-full flex-1 flex-col items-center justify-center overflow-y-auto p-6 text-center"
      : "flex flex-1 flex-col items-center justify-center p-8",
    icon: compact
      ? "mb-4 h-14 w-14 [&_svg]:size-6"
      : "mb-6 h-20 w-20 [&_svg]:size-8",
    title: compact ? "mb-1 text-base" : "mb-2 text-xl",
    description: compact ? "mb-4 text-xs" : "mb-6 text-sm",
    button: compact ? "mb-4" : "mb-8",
    link: compact ? "text-xs" : "text-sm",
  };

  return (
    <div className={cx.root}>
      <div
        className={`flex items-center justify-center rounded-full bg-black/5 [&_svg]:text-black/60 ${cx.icon}`}
      >
        <Camera />
      </div>

      <p className={`font-semibold text-black ${cx.title}`}>
        {t("connection.noCameraConfigured.title")}
      </p>

      <p className={`text-black/60 ${cx.description}`}>
        {t("connection.noCameraConfigured.description")}
      </p>

      {onOpenSettings && (
        <Button
          onClick={onOpenSettings}
          size={compact ? "sm" : "default"}
          className={cx.button}
        >
          {t("connection.noCameraConfigured.openSettings")}
        </Button>
      )}

      <a
        href="https://robopipe.gitbook.io/doc/getting-started/connection"
        target="_blank"
        rel="noopener noreferrer"
        className={`text-center text-primary hover:underline ${cx.link}`}
      >
        {t("connection.learnMore")}
      </a>
    </div>
  );
};
